import { useEffect, useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import moment from 'moment';
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  Stack,
  Button,
  Container,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Box,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  MenuItem,
  Checkbox,
  Tooltip,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import ReactApexChart from 'react-apexcharts';
import Iconify from '../components/iconify';
import { getCurrentIdentity } from '../protectedRoute';
import {
  VENDORS_COLLECTION,
  SETTINGS_COLLECTION,
  SETTINGS_DOC,
  FN_URLS,
  PUBLIC_PAY_BASE,
  TYPE_LABELS,
  TYPE_KEYS,
  STATUS_LABELS,
  STATUS_COLORS,
  METHOD_LABELS,
  KIND_LABELS,
  DEFAULT_SETTINGS,
  money,
  fmtDateEs,
  pricingSnapshot,
  computeTotals,
  paidCents,
  randomToken,
  callFn,
  daysLabel,
  DOC_TYPES,
  DOC_KEYS,
  taxBreakdown,
  depositPercentFor,
  fileToBase64,
  downloadCsv,
} from '../utils/csccVendors';

// ----------------------------------------------------------------------

const db = getFirestore();

const emptyVendor = {
  type: 'emprendedor',
  business: '',
  representative: '',
  phone: '',
  email: '',
  products: '',
  notes: '',
  days: [],
  fullPackage: false,
  daysLocked: false,
  customPrice: '',     // precio especial para este participante (vacío = tarifas del tipo)
  fullPayment: false,  // pago completo antes de la fecha del depósito (sin depósito)
};

const toDate = (ts) => (ts && ts.toDate ? ts.toDate() : ts ? new Date(ts) : null);

function statusOf(v) {
  if (v.status === 'cancelled') return 'cancelled';
  const paid = paidCents(v);
  if (v.totalCents > 0 && paid >= v.totalCents) return 'paid';
  if (v.depositCents > 0 && paid >= v.depositCents) return 'deposit_paid';
  return v.status === 'draft' ? 'draft' : 'pending_deposit';
}

export default function CsccVendorsPage() {
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Editor de participante
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVendor);
  const [saving, setSaving] = useState(false);
  const [sendAfterSave, setSendAfterSave] = useState(true);

  // Detalle / pago manual
  const [detail, setDetail] = useState(null);
  const [payDialog, setPayDialog] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', method: 'cash', note: '' });
  const [busyId, setBusyId] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);

  const uploadDocAdmin = async (v, docType, file) => {
    if (!file) return;
    setUploadingDoc(docType);
    try {
      const dataBase64 = await fileToBase64(file);
      await callFn(FN_URLS.uploadDoc, { vendorId: v.id, docType, fileName: file.name, contentType: file.type, dataBase64 });
      setToast({ severity: 'success', message: `${DOC_TYPES[docType]} guardado.` });
      await load();
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setUploadingDoc(null);
    }
  };

  // Filtros
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sSnap, vSnap] = await Promise.all([
        getDoc(doc(db, SETTINGS_COLLECTION, SETTINGS_DOC)),
        getDocs(query(collection(db, VENDORS_COLLECTION), orderBy('createdAt', 'desc'))).catch(() =>
          getDocs(collection(db, VENDORS_COLLECTION))
        ),
      ]);
      setSettings(sSnap.exists() ? { ...DEFAULT_SETTINGS, ...sSnap.data() } : { ...DEFAULT_SETTINGS, _isDefault: true });
      setVendors(vSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      setToast({ severity: 'error', message: `No se pudo cargar: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---------- Participantes ----------
  const openNew = () => {
    setEditing(null);
    setForm(emptyVendor);
    setSendAfterSave(true);
    setEditorOpen(true);
  };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      type: v.type,
      business: v.business || '',
      representative: v.representative || '',
      phone: v.phone || '',
      email: v.email || '',
      products: v.products || '',
      notes: v.notes || '',
      days: v.days || [],
      fullPackage: v.fullPackage === true,
      daysLocked: v.daysLocked === true,
      customPrice: v.pricing && Number(v.pricing.flatPrice) > 0 ? String(v.pricing.flatPrice) : '',
      fullPayment: Number(v.depositPercent) >= 100,
    });
    setSendAfterSave(false);
    setEditorOpen(true);
  };

  // Totales en vivo dentro del editor (usa el snapshot del participante o la
  // configuración actual si es nuevo / cambió de tipo).
  // Al cambiar el tipo en un participante nuevo, precargar su tarifa única y
  // si paga completo (p. ej. Artesano: $25, sin depósito); ambos son editables.
  useEffect(() => {
    if (!settings || editing || !editorOpen) return;
    const t = (settings.types || {})[form.type] || {};
    setForm((f) => ({ ...f, customPrice: Number(t.flatPrice) > 0 ? String(t.flatPrice) : '', fullPayment: depositPercentFor(settings, form.type) >= 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.type, editorOpen]);

  const editorPricing = useMemo(() => {
    if (!settings) return null;
    const base = editing && editing.type === form.type && editing.pricing ? editing.pricing : pricingSnapshot(settings, form.type);
    const custom = Number(form.customPrice);
    return { ...base, flatPrice: custom > 0 ? custom : 0 };
  }, [settings, editing, form.type, form.customPrice]);
  const editorPct = useMemo(() => {
    if (!settings) return 25;
    if (form.fullPayment) return 100;
    const own = editing && editing.type === form.type && Number(editing.depositPercent) < 100 ? Number(editing.depositPercent) : null;
    const typePct = depositPercentFor(settings, form.type);
    return own || (typePct >= 100 ? (Number(settings.depositPercent) || 25) : typePct);
  }, [settings, editing, form.type, form.fullPayment]);
  const editorTotals = useMemo(() => {
    if (!editorPricing) return null;
    return computeTotals({ pricing: editorPricing, depositPercent: editorPct }, form);
  }, [editorPricing, editorPct, form]);

  const saveVendor = async () => {
    if (!form.business.trim()) { setToast({ severity: 'warning', message: 'Escribe el nombre del negocio.' }); return; }
    if (!form.email.trim() && sendAfterSave) { setToast({ severity: 'warning', message: 'Para enviar el link hace falta el email.' }); return; }
    setSaving(true);
    try {
      const now = Timestamp.now();
      const base = {
        type: form.type,
        business: form.business.trim(),
        representative: form.representative.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        products: form.products.trim(),
        notes: form.notes.trim(),
        daysLocked: form.daysLocked === true,
        updatedAt: now,
      };
      const pct = editorPct;
      const t = computeTotals({ pricing: editorPricing, depositPercent: pct }, form);
      let id;
      if (editing) {
        const update = { ...base, ...t, pricing: editorPricing, depositPercent: pct };
        await updateDoc(doc(db, VENDORS_COLLECTION, editing.id), update);
        id = editing.id;
      } else {
        const ref = await addDoc(collection(db, VENDORS_COLLECTION), {
          ...base,
          ...t,
          pricing: editorPricing,
          depositPercent: pct,
          depositDueDate: settings.depositDueDate,
          balanceDueDate: settings.balanceDueDate,
          eventName: settings.eventName,
          venue: settings.venue,
          agreementUrl: ((settings.types || {})[form.type] || {}).agreementUrl || '',
          cancelTerms: settings.cancelTerms || '',
          emailIntro: settings.emailIntro || '',
          contactName: settings.contactName || '',
          contactPhone: settings.contactPhone || '',
          contactEmail: settings.contactEmail || '',
          token: randomToken(),
          status: 'draft',
          payments: [],
          paidCents: 0,
          linkSentCount: 0,
          createdAt: now,
        });
        id = ref.id;
      }
      setEditorOpen(false);
      if (sendAfterSave && !editing) await sendLink(id, false);
      else setToast({ severity: 'success', message: editing ? 'Participante actualizado.' : 'Participante creado.' });
      await load();
    } catch (e) {
      console.error(e);
      setToast({ severity: 'error', message: e.message });
    } finally {
      setSaving(false);
    }
  };

  const sendLink = async (id, reminder) => {
    setBusyId(id);
    try {
      const r = await callFn(FN_URLS.sendLink, { vendorId: id, reminder });
      setToast(r.ok ? { severity: 'success', message: 'Link enviado por email.' } : { severity: 'error', message: `No se envió: ${r.error}` });
      await load();
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async (v) => {
    const url = `${PUBLIC_PAY_BASE}?t=${v.token}`;
    try { await navigator.clipboard.writeText(url); setToast({ severity: 'success', message: 'Link copiado.' }); }
    catch { window.prompt('Copia el link:', url); }
  };

  const cancelVendor = async (v) => {
    const ok = window.confirm(`¿Cancelar la participación de ${v.business}? Los pagos registrados quedan en el historial.`);
    if (!ok) return;
    const note = window.prompt('Nota (opcional): monto a reembolsar, motivo…', '') || '';
    await updateDoc(doc(db, VENDORS_COLLECTION, v.id), { status: 'cancelled', cancelledAt: Timestamp.now(), cancelNote: note, updatedAt: Timestamp.now() });
    setToast({ severity: 'success', message: 'Participación cancelada.' });
    load();
  };

  const reactivateVendor = async (v) => {
    await updateDoc(doc(db, VENDORS_COLLECTION, v.id), { status: 'pending_deposit', cancelledAt: null, updatedAt: Timestamp.now() });
    load();
  };

  const openPay = (v) => {
    const remaining = Math.max(0, (v.totalCents || 0) - paidCents(v));
    const suggested = paidCents(v) === 0 ? v.depositCents : remaining;
    setPayForm({ amount: (suggested / 100).toFixed(2), method: 'cash', note: '' });
    setPayDialog(v);
  };
  const submitPay = async () => {
    setSaving(true);
    try {
      await callFn(FN_URLS.recordPayment, {
        vendorId: payDialog.id,
        amount: Number(payForm.amount),
        method: payForm.method,
        note: payForm.note,
        recordedBy: (getCurrentIdentity().email) || '',
      });
      setToast({ severity: 'success', message: 'Pago registrado y recibo enviado.' });
      setPayDialog(null);
      await load();
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return vendors.filter((v) => {
      if (filterType !== 'all' && v.type !== filterType) return false;
      if (filterStatus !== 'all' && statusOf(v) !== filterStatus) return false;
      if (s && !`${v.business} ${v.representative} ${v.email} ${v.phone} ${v.products}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [vendors, filterType, filterStatus, search]);

  // ---------- Reporte ----------
  const report = useMemo(() => {
    const active = vendors.filter((v) => v.status !== 'cancelled');
    const sum = (arr, f) => arr.reduce((s, v) => s + (Number(f(v)) || 0), 0);
    const contracted = sum(active, (v) => v.totalCents);
    const collected = sum(vendors, paidCents); // incluye cancelados: el dinero entró
    const pending = sum(active, (v) => Math.max(0, (v.totalCents || 0) - paidCents(v)));
    const today = moment().format('YYYY-MM-DD');
    const overdueDeposit = active.filter((v) => v.depositDueDate && v.depositDueDate < today && paidCents(v) < (v.depositCents || 0) && v.totalCents > 0);
    const overdueBalance = active.filter((v) => v.balanceDueDate && v.balanceDueDate < today && paidCents(v) < (v.totalCents || 0) && paidCents(v) >= (v.depositCents || 0) && v.totalCents > 0);
    const notChosen = active.filter((v) => !(v.totalCents > 0));
    const byType = TYPE_KEYS.map((k) => {
      const list = active.filter((v) => v.type === k);
      return {
        key: k,
        label: TYPE_LABELS[k],
        count: list.length,
        contracted: sum(list, (v) => v.totalCents),
        collected: sum(list, paidCents),
        pending: sum(list, (v) => Math.max(0, (v.totalCents || 0) - paidCents(v))),
        paid: list.filter((v) => statusOf(v) === 'paid').length,
        depositPaid: list.filter((v) => statusOf(v) === 'deposit_paid').length,
      };
    });
    const days = (settings && settings.days) || [];
    const byDay = days.map((d) => ({
      ...d,
      counts: TYPE_KEYS.map((k) => active.filter((v) => v.type === k && (v.fullPackage || (v.days || []).includes(d.key))).length),
      revenue: sum(active.filter((v) => (v.days || []).includes(d.key)), (v) => (v.fullPackage ? Math.round((Number(v.pricing && v.pricing.packagePrice) || 0) * 100 / Math.max(1, (v.days || []).length)) : Math.round((Number(v.pricing && v.pricing.dayPrices && v.pricing.dayPrices[d.key]) || 0) * 100))),
    }));
    const payments = vendors.flatMap((v) => (v.payments || []).map((p) => ({ ...p, vendor: v, date: toDate(p.paidAt) }))).sort((a, b) => (b.date || 0) - (a.date || 0));
    const byMethod = Object.keys(METHOD_LABELS).map((m) => ({ key: m, label: METHOD_LABELS[m], total: sum(payments.filter((p) => p.method === m), (p) => p.amountCents) })).filter((x) => x.total > 0);
    // Cobros por semana para la gráfica de tendencia
    const weekMap = {};
    payments.forEach((p) => { if (p.date) { const w = moment(p.date).startOf('isoWeek').format('YYYY-MM-DD'); weekMap[w] = (weekMap[w] || 0) + p.amountCents; } });
    const weeks = Object.keys(weekMap).sort();
    return { active, contracted, collected, pending, overdueDeposit, overdueBalance, notChosen, byType, byDay, payments, byMethod, weeks, weekMap };
  }, [vendors, settings]);

  const csvRows = useMemo(() => vendors.map((v) => ({
    Negocio: v.business, Tipo: TYPE_LABELS[v.type] || v.type, Representante: v.representative, Telefono: v.phone, Email: v.email,
    Productos: v.products, Dias: daysLabel(v), Total: (v.totalCents || 0) / 100, Deposito: (v.depositCents || 0) / 100,
    IVU_incluido: taxBreakdown(v.totalCents || 0).taxCents / 100,
    Documentos: DOC_KEYS.filter((k) => (v.documents || {})[k]).map((k) => DOC_TYPES[k]).join(' | '),
    Pagado: paidCents(v) / 100, Balance: Math.max(0, (v.totalCents || 0) - paidCents(v)) / 100, Estado: STATUS_LABELS[statusOf(v)],
    AceptoTerminos: v.acceptedTermsAt ? moment(toDate(v.acceptedTermsAt)).format('YYYY-MM-DD HH:mm') : '',
    LinkEnviado: v.linkSentAt ? moment(toDate(v.linkSentAt)).format('YYYY-MM-DD HH:mm') : '', Creado: v.createdAt ? moment(toDate(v.createdAt)).format('YYYY-MM-DD') : '',
  })), [vendors]);

  // ---------- Configuración ----------
  const [cfg, setCfg] = useState(null);
  useEffect(() => { if (settings) setCfg(JSON.parse(JSON.stringify(settings))); }, [settings]);
  const saveSettings = async () => {
    setSaving(true);
    try {
      const clean = { ...cfg };
      delete clean._isDefault;
      clean.depositPercent = Number(clean.depositPercent) || 25;
      clean.days = (clean.days || []).filter((d) => d.key && d.label);
      TYPE_KEYS.forEach((k) => {
        const t = clean.types[k] || {};
        const dp = {};
        clean.days.forEach((d) => { dp[d.key] = Number((t.dayPrices || {})[d.key]) || 0; });
        clean.types[k] = { ...t, dayPrices: dp, packagePrice: Number(t.packagePrice) || 0, flatPrice: Number(t.flatPrice) || 0, depositPercent: Number(t.depositPercent) > 0 ? Number(t.depositPercent) : null, agreementUrl: isHttpUrl(t.agreementUrl) ? t.agreementUrl.trim() : '' };
      });
      clean.updatedAt = Timestamp.now();
      await setDoc(doc(db, SETTINGS_COLLECTION, SETTINGS_DOC), clean);
      setToast({ severity: 'success', message: 'Configuración guardada. Aplica a los participantes nuevos.' });
      await load();
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setSaving(false);
    }
  };
  const setType = (k, patch) => setCfg((c) => ({ ...c, types: { ...c.types, [k]: { ...(c.types[k] || {}), ...patch } } }));
  const [uploadingAgreement, setUploadingAgreement] = useState(null);
  // El acuerdo tiene que subirse al servidor: una ruta local (file:///…) solo
  // existe en la computadora de quien la pegó.
  const uploadAgreement = async (k, file) => {
    if (!file) return;
    setUploadingAgreement(k);
    try {
      const dataBase64 = await fileToBase64(file);
      const { url } = await callFn(FN_URLS.uploadAgreement, { type: k, fileName: file.name, contentType: file.type, dataBase64 });
      setType(k, { agreementUrl: url, agreementName: file.name });
      setToast({ severity: 'success', message: `Acuerdo de ${TYPE_LABELS[k]} subido. Recuerda "Guardar configuración".` });
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setUploadingAgreement(null);
    }
  };
  const isHttpUrl = (u) => /^https?:\/\//i.test(String(u || '').trim());
  const setDayPrice = (k, dayKey, val) => setCfg((c) => ({ ...c, types: { ...c.types, [k]: { ...(c.types[k] || {}), dayPrices: { ...((c.types[k] || {}).dayPrices || {}), [dayKey]: val } } } }));

  // ---------- Render ----------
  if (loading && !settings) {
    return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Container>;
  }

  const dayLabel = (v, k) => (((v.pricing && v.pricing.days) || []).find((d) => d.key === k) || {}).label || k;

  return (
    <>
      <Helmet><title>Vendedores CSCC | BBTC Dashboard</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h4">Vendedores · {settings.eventName}</Typography>
            <Typography variant="body2" color="text.secondary">Emprendedores, carpas de comida y food trucks de The Village. Depósito {settings.depositPercent}% hasta el {fmtDateEs(settings.depositDueDate)} · balance hasta el {fmtDateEs(settings.balanceDueDate)}.</Typography>
          </Box>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openNew}>Nuevo participante</Button>
        </Stack>

        {settings._isDefault && (
          <Alert severity="info" sx={{ mb: 2 }}>Todavía no has guardado la configuración: se están usando las tarifas de los acuerdos (V18 / V16). Revisa la pestaña Configuración y guarda.</Alert>
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Participantes (${vendors.length})`} />
          <Tab label="Reporte" />
          <Tab label="Configuración" />
        </Tabs>

        {/* ---------------- Participantes ---------------- */}
        {tab === 0 && (
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} mb={2}>
                <TextField size="small" label="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 220 }} />
                <TextField size="small" select label="Tipo" value={filterType} onChange={(e) => setFilterType(e.target.value)} sx={{ minWidth: 200 }}>
                  <MenuItem value="all">Todos</MenuItem>
                  {TYPE_KEYS.map((k) => <MenuItem key={k} value={k}>{TYPE_LABELS[k]}</MenuItem>)}
                </TextField>
                <TextField size="small" select label="Estado" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 200 }}>
                  <MenuItem value="all">Todos</MenuItem>
                  {Object.keys(STATUS_LABELS).map((k) => <MenuItem key={k} value={k}>{STATUS_LABELS[k]}</MenuItem>)}
                </TextField>
                <Box flex={1} />
                <Button onClick={() => downloadCsv(`vendedores-cscc-${moment().format('YYYY-MM-DD')}.csv`, csvRows)} disabled={!csvRows.length} variant="outlined" startIcon={<Iconify icon="eva:download-outline" />}>CSV</Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Negocio</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Contacto</TableCell>
                      <TableCell>Días</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Pagado</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Docs</TableCell>
                      <TableCell>Link</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 && (
                      <TableRow><TableCell colSpan={11}><Typography color="text.secondary" align="center" py={3}>Sin participantes{vendors.length ? ' con esos filtros' : ''}. Crea el primero con “Nuevo participante”.</Typography></TableCell></TableRow>
                    )}
                    {filtered.map((v) => {
                      const st = statusOf(v);
                      const paid = paidCents(v);
                      const remaining = Math.max(0, (v.totalCents || 0) - paid);
                      const busy = busyId === v.id;
                      return (
                        <TableRow key={v.id} hover sx={{ opacity: st === 'cancelled' ? 0.6 : 1 }}>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ cursor: 'pointer' }} onClick={() => setDetail(v)}>{v.business}</Typography>
                            <Typography variant="caption" color="text.secondary">{v.products}</Typography>
                          </TableCell>
                          <TableCell><Typography variant="body2">{TYPE_LABELS[v.type] || v.type}</Typography></TableCell>
                          <TableCell>
                            <Typography variant="body2">{v.representative}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">{v.email}</Typography>
                            <Typography variant="caption" color="text.secondary">{v.phone}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{daysLabel(v)}</Typography>
                            {v.daysLocked && <Typography variant="caption" color="text.secondary">fijados por admin</Typography>}
                          </TableCell>
                          <TableCell align="right">{v.totalCents ? money(v.totalCents) : '—'}</TableCell>
                          <TableCell align="right">{money(paid)}</TableCell>
                          <TableCell align="right">{v.totalCents ? money(remaining) : '—'}</TableCell>
                          <TableCell><Chip size="small" label={STATUS_LABELS[st]} color={STATUS_COLORS[st]} /></TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              {DOC_KEYS.map((k) => {
                                const d = (v.documents || {})[k];
                                const optional = k === 'artisanCertificate' && v.type !== 'artesano';
                                if (!d && optional) return null;
                                return (
                                  <Tooltip key={k} title={d ? `${DOC_TYPES[k]} · ${d.name}` : `${DOC_TYPES[k]}: falta`}>
                                    <Box component={d ? 'a' : 'span'} href={d ? d.url : undefined} target="_blank" rel="noreferrer" sx={{ display: 'inline-flex', color: d ? 'success.main' : 'text.disabled' }}>
                                      <Iconify icon={k === 'signedAgreement' ? 'eva:file-text-outline' : k === 'artisanCertificate' ? 'eva:award-outline' : 'eva:briefcase-outline'} width={18} />
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {v.linkSentAt ? (
                              <Tooltip title={v.linkLastResult && !v.linkLastResult.ok ? `Último envío falló: ${v.linkLastResult.error}` : ''}>
                                <Typography variant="caption" color={v.linkLastResult && !v.linkLastResult.ok ? 'error' : 'text.secondary'}>
                                  {moment(toDate(v.linkSentAt)).format('D MMM HH:mm')} · {v.linkSentCount || 1}×
                                </Typography>
                              </Tooltip>
                            ) : <Typography variant="caption" color="text.secondary">sin enviar</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" justifyContent="flex-end" spacing={0}>
                              <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => setDetail(v)}><Iconify icon="eva:eye-outline" /></IconButton></Tooltip>
                              <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(v)}><Iconify icon="eva:edit-outline" /></IconButton></Tooltip>
                              {st !== 'cancelled' && st !== 'paid' && (
                                <Tooltip title={v.linkSentAt ? 'Reenviar link (recordatorio)' : 'Enviar link por email'}>
                                  <span><IconButton size="small" disabled={busy || !v.email} onClick={() => sendLink(v.id, Boolean(v.linkSentAt))}>{busy ? <CircularProgress size={18} /> : <Iconify icon="eva:email-outline" />}</IconButton></span>
                                </Tooltip>
                              )}
                              <Tooltip title="Copiar link"><IconButton size="small" onClick={() => copyLink(v)}><Iconify icon="eva:link-2-outline" /></IconButton></Tooltip>
                              {st !== 'cancelled' && st !== 'paid' && v.totalCents > 0 && (
                                <Tooltip title="Registrar pago (efectivo / ATH)"><IconButton size="small" color="success" onClick={() => openPay(v)}><Iconify icon="eva:credit-card-outline" /></IconButton></Tooltip>
                              )}
                              {st !== 'cancelled'
                                ? <Tooltip title="Cancelar participación"><IconButton size="small" color="error" onClick={() => cancelVendor(v)}><Iconify icon="eva:close-circle-outline" /></IconButton></Tooltip>
                                : <Tooltip title="Reactivar"><IconButton size="small" onClick={() => reactivateVendor(v)}><Iconify icon="eva:refresh-outline" /></IconButton></Tooltip>}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* ---------------- Reporte ---------------- */}
        {tab === 1 && (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              {[
                { label: 'Participantes activos', value: report.active.length, sub: `${vendors.length - report.active.length} cancelados` },
                { label: 'Contratado', value: money(report.contracted), sub: 'suma de espacios activos' },
                { label: 'Cobrado', value: money(report.collected), sub: `${report.payments.length} pagos` },
                { label: 'Por cobrar', value: money(report.pending), sub: `${report.notChosen.length} sin escoger días` },
              ].map((k) => (
                <Grid item xs={6} md={3} key={k.label}>
                  <Card><CardContent>
                    <Typography variant="body2" color="text.secondary">{k.label}</Typography>
                    <Typography variant="h4">{k.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{k.sub}</Typography>
                  </CardContent></Card>
                </Grid>
              ))}
            </Grid>

            {(report.overdueDeposit.length > 0 || report.overdueBalance.length > 0) && (
              <Alert severity="warning">
                {report.overdueDeposit.length > 0 && <div><strong>{report.overdueDeposit.length}</strong> con depósito vencido: {report.overdueDeposit.map((v) => v.business).join(', ')}</div>}
                {report.overdueBalance.length > 0 && <div><strong>{report.overdueBalance.length}</strong> con balance vencido: {report.overdueBalance.map((v) => v.business).join(', ')}</div>}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardHeader title="Cobrado vs. por cobrar por tipo" />
                  <CardContent>
                    <ReactApexChart
                      type="bar"
                      height={300}
                      options={{
                        chart: { stacked: true, toolbar: { show: false } },
                        xaxis: { categories: report.byType.map((t) => t.label) },
                        yaxis: { labels: { formatter: (v) => `$${Math.round(v)}` } },
                        colors: ['#1e3a2a', '#d9a441'],
                        dataLabels: { enabled: false },
                        tooltip: { y: { formatter: (v) => `$${Number(v).toFixed(2)}` } },
                        legend: { position: 'top' },
                      }}
                      series={[
                        { name: 'Cobrado', data: report.byType.map((t) => t.collected / 100) },
                        { name: 'Por cobrar', data: report.byType.map((t) => t.pending / 100) },
                      ]}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardHeader title="Participantes por tipo" />
                  <CardContent>
                    {report.active.length ? (
                      <ReactApexChart type="donut" height={300}
                        options={{ labels: report.byType.map((t) => t.label), legend: { position: 'bottom' }, colors: ['#1e3a2a', '#d9a441', '#7a9e7e'], dataLabels: { enabled: true } }}
                        series={report.byType.map((t) => t.count)} />
                    ) : <Typography color="text.secondary">Sin datos</Typography>}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card>
              <CardHeader title="Resumen por tipo" />
              <TableContainer><Table size="small">
                <TableHead><TableRow>
                  <TableCell>Tipo</TableCell><TableCell align="right">Participantes</TableCell><TableCell align="right">Depósito pagado</TableCell><TableCell align="right">Pagados completos</TableCell><TableCell align="right">Contratado</TableCell><TableCell align="right">Cobrado</TableCell><TableCell align="right">Por cobrar</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {report.byType.map((t) => (
                    <TableRow key={t.key}><TableCell>{t.label}</TableCell><TableCell align="right">{t.count}</TableCell><TableCell align="right">{t.depositPaid}</TableCell><TableCell align="right">{t.paid}</TableCell><TableCell align="right">{money(t.contracted)}</TableCell><TableCell align="right">{money(t.collected)}</TableCell><TableCell align="right">{money(t.pending)}</TableCell></TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 'bold' } }}><TableCell>Total</TableCell><TableCell align="right">{report.active.length}</TableCell><TableCell align="right">{report.byType.reduce((s, t) => s + t.depositPaid, 0)}</TableCell><TableCell align="right">{report.byType.reduce((s, t) => s + t.paid, 0)}</TableCell><TableCell align="right">{money(report.contracted)}</TableCell><TableCell align="right">{money(report.byType.reduce((s, t) => s + t.collected, 0))}</TableCell><TableCell align="right">{money(report.pending)}</TableCell></TableRow>
                </TableBody>
              </Table></TableContainer>
            </Card>

            <Card>
              <CardHeader title="Espacios por día" subheader="Cuántos negocios de cada tipo estarán cada día (incluye paquetes de 4 días)" />
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Día</TableCell>{TYPE_KEYS.map((k) => <TableCell key={k} align="right">{TYPE_LABELS[k]}</TableCell>)}<TableCell align="right">Total espacios</TableCell></TableRow></TableHead>
                <TableBody>
                  {report.byDay.map((d) => (
                    <TableRow key={d.key}><TableCell>{d.label}</TableCell>{d.counts.map((c, i) => <TableCell key={i} align="right">{c}</TableCell>)}<TableCell align="right"><strong>{d.counts.reduce((s, c) => s + c, 0)}</strong></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardHeader title="Cobrado por método" />
                  <CardContent>
                    {report.byMethod.length ? report.byMethod.map((m) => (
                      <Stack key={m.key} direction="row" justifyContent="space-between" py={0.5}><Typography>{m.label}</Typography><Typography fontWeight="bold">{money(m.total)}</Typography></Stack>
                    )) : <Typography color="text.secondary">Sin pagos todavía</Typography>}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardHeader title="Cobros por semana" />
                  <CardContent>
                    {report.weeks.length ? (
                      <ReactApexChart type="area" height={220}
                        options={{ chart: { toolbar: { show: false } }, xaxis: { categories: report.weeks.map((w) => moment(w).format('D MMM')) }, yaxis: { labels: { formatter: (v) => `$${Math.round(v)}` } }, colors: ['#1e3a2a'], dataLabels: { enabled: false }, stroke: { curve: 'smooth', width: 2 }, tooltip: { y: { formatter: (v) => `$${Number(v).toFixed(2)}` } } }}
                        series={[{ name: 'Cobrado', data: report.weeks.map((w) => report.weekMap[w] / 100) }]} />
                    ) : <Typography color="text.secondary">Sin pagos todavía</Typography>}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card>
              <CardHeader title="Historial de pagos" action={
                <Button onClick={() => downloadCsv(`pagos-vendedores-cscc-${moment().format('YYYY-MM-DD')}.csv`, report.payments.map((p) => ({ Fecha: p.date ? moment(p.date).format('YYYY-MM-DD HH:mm') : '', Negocio: p.vendor.business, Tipo: TYPE_LABELS[p.vendor.type], Concepto: KIND_LABELS[p.kind] || p.kind, Metodo: METHOD_LABELS[p.method] || p.method, Monto: (p.amountCents || 0) / 100, Stripe: p.stripePaymentIntentId || '', Nota: p.note || '' })))} disabled={!report.payments.length} size="small" startIcon={<Iconify icon="eva:download-outline" />}>CSV</Button>
              } />
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Fecha</TableCell><TableCell>Negocio</TableCell><TableCell>Concepto</TableCell><TableCell>Método</TableCell><TableCell align="right">Monto</TableCell><TableCell>Referencia</TableCell></TableRow></TableHead>
                <TableBody>
                  {report.payments.length === 0 && <TableRow><TableCell colSpan={6}><Typography color="text.secondary" align="center" py={2}>Sin pagos todavía</Typography></TableCell></TableRow>}
                  {report.payments.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell>{p.date ? moment(p.date).format('D MMM YYYY HH:mm') : '—'}</TableCell>
                      <TableCell>{p.vendor.business} <Typography variant="caption" color="text.secondary">({TYPE_LABELS[p.vendor.type]})</Typography></TableCell>
                      <TableCell>{KIND_LABELS[p.kind] || p.kind}</TableCell>
                      <TableCell>{METHOD_LABELS[p.method] || p.method}{p.last4 ? ` ···· ${p.last4}` : ''}</TableCell>
                      <TableCell align="right">{money(p.amountCents)}</TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{p.stripePaymentIntentId || p.note || ''}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </Card>
          </Stack>
        )}

        {/* ---------------- Configuración ---------------- */}
        {tab === 2 && cfg && (
          <Stack spacing={3}>
            <Alert severity="info">Los cambios aplican a los participantes que crees después de guardar. Los que ya existen conservan las tarifas y fechas con que se crearon (edítalos uno a uno si hace falta).</Alert>
            <Card>
              <CardHeader title="Evento" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Nombre del evento" value={cfg.eventName || ''} onChange={(e) => setCfg({ ...cfg, eventName: e.target.value })} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Lugar" value={cfg.venue || ''} onChange={(e) => setCfg({ ...cfg, venue: e.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Depósito (%)" value={cfg.depositPercent} onChange={(e) => setCfg({ ...cfg, depositPercent: e.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth type="date" label="Depósito vence" InputLabelProps={{ shrink: true }} value={cfg.depositDueDate || ''} onChange={(e) => setCfg({ ...cfg, depositDueDate: e.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth type="date" label="Balance vence" InputLabelProps={{ shrink: true }} value={cfg.balanceDueDate || ''} onChange={(e) => setCfg({ ...cfg, balanceDueDate: e.target.value })} /></Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Días del evento" subheader="La fecha identifica el día; la etiqueta es lo que ve el vendedor." action={<Button size="small" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setCfg({ ...cfg, days: [...(cfg.days || []), { key: '', label: '' }] })}>Añadir día</Button>} />
              <CardContent>
                <Stack spacing={1.5}>
                  {(cfg.days || []).map((d, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                      <TextField size="small" type="date" label="Fecha" InputLabelProps={{ shrink: true }} value={d.key} onChange={(e) => { const days = [...cfg.days]; days[i] = { ...d, key: e.target.value }; setCfg({ ...cfg, days }); }} />
                      <TextField size="small" label="Etiqueta" value={d.label} onChange={(e) => { const days = [...cfg.days]; days[i] = { ...d, label: e.target.value }; setCfg({ ...cfg, days }); }} sx={{ flex: 1 }} />
                      <IconButton size="small" color="error" onClick={() => setCfg({ ...cfg, days: cfg.days.filter((_, j) => j !== i) })}><Iconify icon="eva:trash-2-outline" /></IconButton>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Tarifas por tipo de espacio" subheader="Precio por día y precio del paquete de todos los días (sustituye la suma de días). Los precios incluyen el IVU (11.5 %): se desglosa hacia atrás para que el total cuadre exacto." />
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Tipo</TableCell>{(cfg.days || []).map((d) => <TableCell key={d.key || d.label} align="right">{d.label || d.key}</TableCell>)}<TableCell align="right">Paquete todos los días</TableCell><TableCell align="right">Suma de días</TableCell><TableCell align="right">Tarifa única</TableCell><TableCell align="right">Depósito %</TableCell><TableCell>Acuerdo (PDF que firma el vendedor)</TableCell><TableCell>Activo</TableCell></TableRow></TableHead>
                <TableBody>
                  {TYPE_KEYS.map((k) => {
                    const t = cfg.types[k] || {};
                    const sumDays = (cfg.days || []).reduce((s, d) => s + (Number((t.dayPrices || {})[d.key]) || 0), 0);
                    return (
                      <TableRow key={k}>
                        <TableCell>{TYPE_LABELS[k]}</TableCell>
                        {(cfg.days || []).map((d) => <TableCell key={d.key || d.label} align="right"><TextField size="small" type="number" value={(t.dayPrices || {})[d.key] ?? ''} onChange={(e) => setDayPrice(k, d.key, e.target.value)} sx={{ width: 90 }} InputProps={{ startAdornment: '$' }} /></TableCell>)}
                        <TableCell align="right"><TextField size="small" type="number" value={t.packagePrice ?? ''} onChange={(e) => setType(k, { packagePrice: e.target.value })} sx={{ width: 100 }} InputProps={{ startAdornment: '$' }} /></TableCell>
                        <TableCell align="right"><Typography variant="body2" color="text.secondary">${sumDays.toFixed(2)}{t.packagePrice ? ` (ahorro $${(sumDays - Number(t.packagePrice)).toFixed(2)})` : ''}</Typography></TableCell>
                        <TableCell align="right"><Tooltip title="Si hay tarifa única se cobra ese monto por todo el evento, sin importar los días (p. ej. artesanos $25)."><TextField size="small" type="number" value={t.flatPrice ?? ''} onChange={(e) => setType(k, { flatPrice: e.target.value })} sx={{ width: 90 }} InputProps={{ startAdornment: '$' }} /></Tooltip></TableCell>
                        <TableCell align="right"><Tooltip title="Vacío = usa el % general. 100 = pago completo en la fecha del depósito."><TextField size="small" type="number" placeholder={String(cfg.depositPercent)} value={t.depositPercent ?? ''} onChange={(e) => setType(k, { depositPercent: e.target.value })} sx={{ width: 80 }} /></Tooltip></TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {isHttpUrl(t.agreementUrl)
                              ? <Typography variant="caption" sx={{ maxWidth: 140 }} noWrap><a href={t.agreementUrl} target="_blank" rel="noreferrer">{t.agreementName || 'Ver PDF'}</a></Typography>
                              : <Typography variant="caption" color={t.agreementUrl ? 'error' : 'text.disabled'}>{t.agreementUrl ? 'Ruta inválida (sube el PDF)' : 'Sin acuerdo'}</Typography>}
                            <Button size="small" component="label" disabled={uploadingAgreement === k}>
                              {uploadingAgreement === k ? <CircularProgress size={14} /> : (isHttpUrl(t.agreementUrl) ? 'Reemplazar' : 'Subir PDF')}
                              <input hidden type="file" accept="application/pdf" onChange={(e) => { uploadAgreement(k, e.target.files && e.target.files[0]); e.target.value = ''; }} />
                            </Button>
                            {isHttpUrl(t.agreementUrl) && <IconButton size="small" onClick={() => setType(k, { agreementUrl: '', agreementName: '' })}><Iconify icon="eva:close-outline" width={16} /></IconButton>}
                          </Stack>
                        </TableCell>
                        <TableCell><Switch checked={t.enabled !== false} onChange={(e) => setType(k, { enabled: e.target.checked })} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table></TableContainer>
            </Card>

            <Card>
              <CardHeader title="Textos y contacto" subheader="Se incluyen en el email y en la página de pago." />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Introducción del email" value={cfg.emailIntro || ''} onChange={(e) => setCfg({ ...cfg, emailIntro: e.target.value })} /></Grid>
                  <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Condiciones de cancelación / reembolso" value={cfg.cancelTerms || ''} onChange={(e) => setCfg({ ...cfg, cancelTerms: e.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Contacto · nombre" value={cfg.contactName || ''} onChange={(e) => setCfg({ ...cfg, contactName: e.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Contacto · teléfono" value={cfg.contactPhone || ''} onChange={(e) => setCfg({ ...cfg, contactPhone: e.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Contacto · email" value={cfg.contactEmail || ''} onChange={(e) => setCfg({ ...cfg, contactEmail: e.target.value })} /></Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box>
              <Button variant="contained" size="large" disabled={saving} onClick={saveSettings}>{saving ? <CircularProgress size={22} /> : 'Guardar configuración'}</Button>
            </Box>
          </Stack>
        )}
      </Container>

      {/* ---------------- Editor ---------------- */}
      <Dialog open={editorOpen} onClose={() => !saving && setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar participante' : 'Nuevo participante'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Tipo de espacio" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPE_KEYS.filter((k) => ((settings.types || {})[k] || {}).enabled !== false || k === form.type).map((k) => <MenuItem key={k} value={k}>{TYPE_LABELS[k]}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}><TextField fullWidth required label="Negocio" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Representante" value={form.representative} onChange={(e) => setForm({ ...form, representative: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label={['emprendedor', 'artesano'].includes(form.type) ? 'Productos que ofrecerá' : 'Tipo de comida / menú'} value={form.products} onChange={(e) => setForm({ ...form, products: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Días</Typography>
              <Typography variant="caption" color="text.secondary">{editorPricing && editorPricing.flatPrice > 0 ? `Tarifa única de $${Number(editorPricing.flatPrice).toFixed(2)} por todo el evento; los días son solo informativos (para saber quién va cada día).` : 'Déjalos en blanco para que el vendedor escoja en el link. Si los marcas aquí, el vendedor los verá preseleccionados y podrá cambiarlos hasta que pague (o fíjalos con el switch).'}</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                {(editorPricing ? editorPricing.days : []).map((d) => (
                  <FormControlLabel key={d.key} disabled={form.fullPackage} control={<Checkbox checked={form.fullPackage || form.days.includes(d.key)} onChange={(e) => setForm({ ...form, days: e.target.checked ? [...form.days, d.key] : form.days.filter((k) => k !== d.key) })} />} label={editorPricing.flatPrice > 0 ? d.label : `${d.label} · $${Number(editorPricing.dayPrices[d.key] || 0).toFixed(2)}`} />
                ))}
                {!(editorPricing && editorPricing.flatPrice > 0) && <FormControlLabel control={<Switch checked={form.fullPackage} onChange={(e) => setForm({ ...form, fullPackage: e.target.checked })} />} label={`Paquete todos los días · $${Number(editorPricing ? editorPricing.packagePrice : 0).toFixed(2)}`} />}
                <FormControlLabel control={<Switch checked={form.daysLocked} onChange={(e) => setForm({ ...form, daysLocked: e.target.checked })} />} label="Fijar días (el vendedor no puede cambiarlos)" />
              </Stack>
              {editorTotals && editorTotals.totalCents > 0 && (
                <Alert severity="success" icon={false} sx={{ mt: 1 }}>
                  Total {money(editorTotals.totalCents)} (incluye IVU {money(taxBreakdown(editorTotals.totalCents).taxCents)})
                  {editorTotals.balanceCents === 0
                    ? <> · Pago completo (vence {fmtDateEs((editing && editing.depositDueDate) || settings.depositDueDate)})</>
                    : <> · Depósito {money(editorTotals.depositCents)} (vence {fmtDateEs((editing && editing.depositDueDate) || settings.depositDueDate)}) · Balance {money(editorTotals.balanceCents)} (vence {fmtDateEs((editing && editing.balanceDueDate) || settings.balanceDueDate)})</>}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Precio y forma de pago</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <TextField fullWidth type="number" label="Precio especial ($)" value={form.customPrice} onChange={(e) => setForm({ ...form, customPrice: e.target.value })} InputProps={{ startAdornment: '$' }} inputProps={{ step: '0.01', min: 0 }}
                    helperText={Number(form.customPrice) > 0 ? 'Este monto sustituye las tarifas por día (IVU incluido).' : 'Vacío = se cobra según los días marcados.'} />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <FormControlLabel control={<Switch checked={form.fullPayment} onChange={(e) => setForm({ ...form, fullPayment: e.target.checked })} />} label={`Pago completo, sin depósito (vence el ${fmtDateEs((editing && editing.depositDueDate) || settings.depositDueDate)})`} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Notas internas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Grid>
            {!editing && <Grid item xs={12}><FormControlLabel control={<Switch checked={sendAfterSave} onChange={(e) => setSendAfterSave(e.target.checked)} />} label="Enviar el link de pago por email al guardar" /></Grid>}
            {editing && paidCents(editing) > 0 && <Grid item xs={12}><Alert severity="warning">Este participante ya pagó {money(paidCents(editing))}. Si cambias los días, el balance se recalcula sobre el nuevo total.</Alert></Grid>}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={saveVendor} disabled={saving}>{saving ? <CircularProgress size={20} /> : (editing ? 'Guardar' : (sendAfterSave ? 'Crear y enviar link' : 'Crear'))}</Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Detalle ---------------- */}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        {detail && (() => {
          const v = vendors.find((x) => x.id === detail.id) || detail;
          const st = statusOf(v);
          const paid = paidCents(v);
          return (
            <>
              <DialogTitle>{v.business} <Chip size="small" label={STATUS_LABELS[st]} color={STATUS_COLORS[st]} sx={{ ml: 1 }} /></DialogTitle>
              <DialogContent dividers>
                <Stack spacing={1}>
                  <Row k="Tipo" v={TYPE_LABELS[v.type] || v.type} />
                  <Row k="Representante" v={v.representative} />
                  <Row k="Email" v={v.email} />
                  <Row k="Teléfono" v={v.phone} />
                  <Row k={['emprendedor', 'artesano'].includes(v.type) ? 'Productos' : 'Menú'} v={v.products} />
                  <Row k="Electricidad" v={v.electricity} />
                  <Row k="Agua" v={v.water} />
                  <Divider />
                  <Row k="Días" v={v.fullPackage ? `Paquete 4 días (${(v.pricing.days || []).map((d) => d.label).join(', ')})` : (v.days || []).map((k) => `${dayLabel(v, k)} $${Number(v.pricing.dayPrices[k] || 0).toFixed(2)}`).join(' · ') || 'Por escoger'} />
                  <Row k="Total" v={v.totalCents ? `${money(v.totalCents)} (incluye IVU ${money(taxBreakdown(v.totalCents).taxCents)})` : '—'} />
                  <Row k={`Depósito (${v.depositPercent}%)`} v={`${money(v.depositCents)} · vence ${fmtDateEs(v.depositDueDate)}`} />
                  <Row k="Balance" v={`${money(v.balanceCents)} · vence ${fmtDateEs(v.balanceDueDate)}`} />
                  <Row k="Pagado" v={money(paid)} />
                  <Row k="Pendiente" v={money(Math.max(0, (v.totalCents || 0) - paid))} />
                  <Divider />
                  <Row k="Aceptó condiciones" v={v.acceptedTermsAt ? `${moment(toDate(v.acceptedTermsAt)).format('D MMM YYYY HH:mm')} (${v.acceptedTermsName || ''})` : 'Todavía no'} />
                  <Row k="Link" v={`${PUBLIC_PAY_BASE}?t=${v.token}`} />
                  <Row k="Link enviado" v={v.linkSentAt ? `${moment(toDate(v.linkSentAt)).format('D MMM YYYY HH:mm')} · ${v.linkSentCount || 1} veces` : 'Sin enviar'} />
                  {v.notes && <Row k="Notas" v={v.notes} />}
                  {v.cancelNote && <Row k="Cancelación" v={v.cancelNote} />}
                  <Divider />
                  <Typography variant="subtitle2">Documentos</Typography>
                  {DOC_KEYS.map((k) => {
                    const d = (v.documents || {})[k];
                    return (
                      <Stack key={k} direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>{DOC_TYPES[k]}</Typography>
                        {d ? <Typography variant="body2"><a href={d.url} target="_blank" rel="noreferrer">{d.name}</a> · {d.uploadedAt ? moment(toDate(d.uploadedAt)).format('D MMM YYYY') : ''}</Typography> : <Typography variant="body2" color="text.disabled">falta</Typography>}
                        <Button size="small" component="label" disabled={uploadingDoc === k}>
                          {uploadingDoc === k ? <CircularProgress size={14} /> : (d ? 'Reemplazar' : 'Subir')}
                          <input hidden type="file" accept="application/pdf,image/*" onChange={(e) => uploadDocAdmin(v, k, e.target.files && e.target.files[0])} />
                        </Button>
                      </Stack>
                    );
                  })}
                  <Divider />
                  <Typography variant="subtitle2">Pagos</Typography>
                  {(v.payments || []).length === 0 && <Typography variant="body2" color="text.secondary">Sin pagos</Typography>}
                  {(v.payments || []).map((p, i) => (
                    <Typography key={i} variant="body2">{p.paidAt ? moment(toDate(p.paidAt)).format('D MMM YYYY HH:mm') : ''} · {KIND_LABELS[p.kind] || p.kind} · {METHOD_LABELS[p.method] || p.method}{p.last4 ? ` ···· ${p.last4}` : ''} · <strong>{money(p.amountCents)}</strong>{p.note ? ` · ${p.note}` : ''}</Typography>
                  ))}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => copyLink(v)}>Copiar link</Button>
                {st !== 'cancelled' && st !== 'paid' && v.email && <Button onClick={() => sendLink(v.id, Boolean(v.linkSentAt))} disabled={busyId === v.id}>{v.linkSentAt ? 'Reenviar link' : 'Enviar link'}</Button>}
                {st !== 'cancelled' && st !== 'paid' && v.totalCents > 0 && <Button variant="contained" color="success" onClick={() => { setDetail(null); openPay(v); }}>Registrar pago</Button>}
                <Button onClick={() => setDetail(null)}>Cerrar</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ---------------- Pago manual ---------------- */}
      <Dialog open={Boolean(payDialog)} onClose={() => !saving && setPayDialog(null)} maxWidth="xs" fullWidth>
        {payDialog && (
          <>
            <DialogTitle>Registrar pago · {payDialog.business}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">Total {money(payDialog.totalCents)} · pagado {money(paidCents(payDialog))} · pendiente {money(Math.max(0, payDialog.totalCents - paidCents(payDialog)))}. Se enviará un recibo por email.</Typography>
                <TextField label="Monto" type="number" inputProps={{ step: '0.01', min: 0 }} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} InputProps={{ startAdornment: '$' }} />
                <TextField select label="Método" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
                  {['cash', 'ath', 'other'].map((m) => <MenuItem key={m} value={m}>{METHOD_LABELS[m]}</MenuItem>)}
                </TextField>
                <TextField label="Nota (opcional)" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPayDialog(null)} disabled={saving}>Cancelar</Button>
              <Button variant="contained" onClick={submitPay} disabled={saving || !(Number(payForm.amount) > 0)}>{saving ? <CircularProgress size={20} /> : 'Registrar'}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : null}
      </Snackbar>
    </>
  );
}

function Row({ k, v }) {
  if (v === undefined || v === null || v === '') return null;
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>{k}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{v}</Typography>
    </Stack>
  );
}
